/**
 * Position Ordering — pick the tightest or widest of 4 positions.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import { type Position } from "@/features/quiz/lib/preflopRanges";
import {
  type PositionOrderingQuestion,
  generateQuestion,
} from "@/features/quiz/lib/positionOrderingQuiz";

export function QuizPositionOrdering() {
  const { question, revealedAnswer, isCorrect, correctAnswer, stats, submit, next } =
    useQuizSession<PositionOrderingQuestion, Position>({
      quizType: "position_ordering",
      generate: generateQuestion,
      getCorrectAnswer: (q) => q.answer,
      getQuestionPayload: (q) => ({ positions: q.positions, prompt: q.prompt }),
    });

  const explanation =
    question.prompt === "widest"
      ? "BTN opens the widest range (any pair, any suited ace/king, plus offsuit connectors T9o/98o that no other seat plays)."
      : "Early position plays the tightest range: only premiums (77+, AJs+, KQs, AQo+). With many players left to act, you need a hand that's a favorite against random.";

  return (
    <QuizLayout
      Icon={Brain}
      title="Position Ordering"
      subtitle="Pick the tightest or widest of 4 positions"
      description="Tests your fluency with the full preflop position ranking: tighter early → wider late. Each question picks one EARLY position, one MIDDLE position, plus SB and BTN; the prompt then asks which is the most extreme in one direction."
      stats={stats}
    >
      <p className="text-center text-text text-base">
        Which of these positions opens the <strong>{question.prompt}</strong> range?
      </p>

      <div className="grid grid-cols-2 gap-3">
        {question.positions.map((pos) => (
          <QuizOption
            key={pos}
            value={pos}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
            size="lg"
          >
            <span className="text-lg">{pos}</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{correctAnswer}</strong> (the {question.prompt})
            </>
          }
          rationale={explanation}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
